import React, { useState, useEffect, useMemo } from "react";
import styled from "styled-components";

type OptionType = {
  label: string;
  value: string;
  isOnlyFilterFromStart?: boolean;
};

type SearchByColumnProps = {
  options: OptionType[];
  data: any[];
  onFiltered: (filteredList: any[]) => void;
  placeholder?: string;
};

const SearchContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const SearchLabel = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: #4b5563;
`;

const SearchSelect = styled.select`
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 14px;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
  }
`;

const SearchInputWrapper = styled.div`
  position: relative;
  flex: 1;
  min-width: 250px;
  max-width: 380px;
`;

const SearchInput = styled.input`
  width: 100%;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 8px 36px 8px 12px;
  font-size: 14px;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
  }
`;

const ClearButton = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  padding: 4px;
  
  &:hover {
    color: #6b7280;
  }
`;

const ResultCount = styled.span<{ hasResults: boolean }>`
  font-size: 14px;
  color: ${props => props.hasResults ? '#16a34a' : '#dc2626'};
  font-weight: 500;
`;

const SearchByColumn: React.FC<SearchByColumnProps> = ({
  options,
  data,
  onFiltered,
  placeholder = "Search...",
}) => {
  const [selectedKey, setSelectedKey] = useState(options[0]?.value || "");
  const [searchText, setSearchText] = useState("");
  const [isFocused, setIsFocused] = useState(false);

  const selectedOption = useMemo(
    () => options.find((opt) => opt.value === selectedKey),
    [options, selectedKey]
  );

  const filteredData = useMemo(() => {
    if (!searchText.trim()) return data;

    const keyword = searchText.toLowerCase();
    const isOnlyFilterFromStart = selectedOption?.isOnlyFilterFromStart;

    return data.filter((item) => {
      const value = String(item?.[selectedKey] || "").toLowerCase();
      return isOnlyFilterFromStart
        ? value.startsWith(keyword)
        : value.includes(keyword);
    });
  }, [data, selectedKey, searchText, selectedOption]);

  useEffect(() => {
    onFiltered(filteredData);
  }, [searchText]);

  return (
    <SearchContainer>
      <SearchLabel>Search by</SearchLabel>

      <SearchSelect
        value={selectedKey}
        onChange={(e) => {
          setSelectedKey(e.target.value);
          setSearchText("");
        }}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </SearchSelect>

      <SearchInputWrapper>
        <SearchInput
          type="text"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder={placeholder}
        />
        {searchText && (
          <ClearButton
            onClick={() => setSearchText("")}
            aria-label="Clear search"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </ClearButton>
        )}
      </SearchInputWrapper>

      {searchText && (
        <ResultCount hasResults={filteredData.length > 0}>
          {filteredData.length} result{filteredData.length !== 1 ? 's' : ''}
        </ResultCount>
      )}
    </SearchContainer>
  );
};

export default SearchByColumn;